import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getAppServices } from "../../client/services/services";
import { useAuth } from "../internal/AuthProvider";
// Note: CSS is now loaded through theme system - see theme-styles/change-password-modal-template.css

interface ChangePasswordProps {
    open: boolean;
    onClose: () => void;
}

export default function ChangePassword({ open, onClose }: ChangePasswordProps) {
    // ============================================================================
    // State & Hooks
    // ============================================================================
    const { isAuthenticated } = useAuth();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [currentPasswordError, setCurrentPasswordError] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [successTimer, setSuccessTimer] = useState(0);
    const services = getAppServices();

    // Success timer countdown
    useEffect(() => {
        if (successTimer > 0) {
            const timer = setInterval(() => {
                setSuccessTimer(prev => {
                    if (prev <= 1) {
                        onClose();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [successTimer, onClose]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setCurrentPasswordError("");
            setPasswordError("");
            setConfirmPasswordError("");
            setError("");
            setSuccess(false);
            setSuccessTimer(0);
        }
    }, [open]);

    // ============================================================================
    // Validation Functions
    // ============================================================================
    const validateCurrentPassword = (passwordValue: string): boolean => {
        if (!passwordValue.trim()) {
            setCurrentPasswordError("Current password is required");
            return false;
        }
        setCurrentPasswordError("");
        return true;
    };

    const validatePassword = (passwordValue: string): boolean => {
        if (!passwordValue.trim()) {
            setPasswordError("Password is required");
            return false;
        }
        if (passwordValue.length < 8) {
            setPasswordError("Password must be at least 8 characters");
            return false;
        }
        if (!/[a-z]/.test(passwordValue)) {
            setPasswordError("Password must contain at least one lowercase letter");
            return false;
        }
        if (!/[A-Z]/.test(passwordValue)) {
            setPasswordError("Password must contain at least one uppercase letter");
            return false;
        }
        if (!/[0-9]/.test(passwordValue)) {
            setPasswordError("Password must contain at least one number");
            return false;
        }
        if (!/[^a-zA-Z0-9]/.test(passwordValue)) {
            setPasswordError("Password must contain at least one special character");
            return false;
        }
        setPasswordError("");
        return true;
    };

    const validateConfirmPassword = (confirmValue: string, passwordValue: string): boolean => {
        if (!confirmValue.trim()) {
            setConfirmPasswordError("Please confirm your password");
            return false;
        }
        if (confirmValue !== passwordValue) {
            setConfirmPasswordError("Passwords do not match");
            return false;
        }
        setConfirmPasswordError("");
        return true;
    };

    // ============================================================================
    // Handler Functions
    // ============================================================================
    const wrapApiCall = async <T = void>(apiCall: () => Promise<T>, onSuccess?: () => void, defaultError?: string) => {
        setIsLoading(true);
        setError("");
        try {
            await apiCall();
            if (onSuccess) onSuccess();
            setError("");
        } catch (error: any) {
            setError(error.message || defaultError || "An unexpected error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePassword = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        // Only validate current password if it's provided (user has a password)
        const needsCurrentPassword = currentPassword.trim() !== "";
        const isCurrentValid = !needsCurrentPassword || validateCurrentPassword(currentPassword);
        const isPasswordValid = validatePassword(newPassword);
        const isConfirmValid = validateConfirmPassword(confirmPassword, newPassword);
        if (!isCurrentValid || !isPasswordValid || !isConfirmValid) {
            return;
        }
        if (needsCurrentPassword && currentPassword === newPassword) {
            setError("New password must be different from current password");
            return;
        }
        await wrapApiCall(
            () => services.auth.changePassword(needsCurrentPassword ? currentPassword : undefined, newPassword),
            () => {
                setSuccess(true);
                setSuccessTimer(3);
            },
            "Failed to change password. Please try again."
        );
    };

    const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(e.target.value);
        if (currentPasswordError) validateCurrentPassword(e.target.value);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);
        if (passwordError) validatePassword(e.target.value);
        if (confirmPassword && confirmPasswordError) validateConfirmPassword(confirmPassword, e.target.value);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (confirmPasswordError) validateConfirmPassword(e.target.value, newPassword);
    };

    // ============================================================================
    // Render Functions
    // ============================================================================
    // Don't show modal if user is not authenticated
    if (!isAuthenticated) {
        return null;
    }
    const commonTextFieldProps = { margin: "normal" as const, required: true, fullWidth: true, disabled: isLoading };

    if (success) {
        return (
            <Modal open={open} onClose={onClose}>
                <Box className="change-password-container">
                    <Typography component="h1" variant="h5" className="change-password-title" sx={{ textAlign: 'center', mb: 2 }}>
                        Password Changed Successfully
                    </Typography>
                    <Typography className="change-password-description" sx={{ textAlign: 'center', mb: 3 }}>
                        Your password has been changed successfully. This window will close automatically in {successTimer} seconds.
                    </Typography>
                    <Button fullWidth variant="contained" className="change-password-button" onClick={onClose} sx={{ mt: 2 }}>
                        Close
                    </Button>
                </Box>
            </Modal>
        );
    }

    return (
        <Modal open={open} onClose={onClose}>
            <Box className="change-password-container">
                <Typography component="h1" variant="h5" className="change-password-title" sx={{ textAlign: 'center', mb: 3 }}>
                    {currentPassword.trim() === "" ? "Set Password" : "Change Password"}
                </Typography>
                <Box component="form" className="change-password-form" onSubmit={handleChangePassword}>
                    {currentPassword.trim() !== "" && (
                        <TextField
                            {...commonTextFieldProps}
                            name="currentPassword"
                            label="Current Password"
                            type="password"
                            id="currentPassword"
                            autoComplete="current-password"
                            value={currentPassword}
                            onChange={handleCurrentPasswordChange}
                            onBlur={() => validateCurrentPassword(currentPassword)}
                            error={!!currentPasswordError}
                            helperText={currentPasswordError}
                        />
                    )}
                    <TextField
                        {...commonTextFieldProps}
                        name="newPassword"
                        label={currentPassword.trim() === "" ? "Password" : "New Password"}
                        type="password"
                        id="newPassword"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={handlePasswordChange}
                        onBlur={() => validatePassword(newPassword)}
                        error={!!passwordError}
                        helperText={passwordError}
                    />
                    <TextField
                        {...commonTextFieldProps}
                        name="confirmPassword"
                        label={currentPassword.trim() === "" ? "Confirm Password" : "Confirm New Password"}
                        type="password"
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        onBlur={() => validateConfirmPassword(confirmPassword, newPassword)}
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError}
                    />
                    {error && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className="change-password-button"
                        disabled={isLoading}
                        sx={{ mt: 2, mb: 2 }}
                    >
                        {isLoading ? (currentPassword.trim() === "" ? "Setting Password..." : "Changing Password...") : (currentPassword.trim() === "" ? "Set Password" : "Change Password")}
                    </Button>
                    <Button fullWidth variant="text" onClick={onClose} sx={{ mt: 1, textTransform: 'none' }} className="change-password-cancel-button">
                        Cancel
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

